/**
 * CashInPage — citizen self-service top-up via Paystack Inline popup.
 *
 * Two-step flow (replaces the deterministic single-shot path):
 *   1. POST /v1/wallet/wallets-mine/cash-ins/initialize  → { reference, accessCode, publicKey }
 *   2. Open Paystack Inline popup with `PaystackPop.setup({ key, ref, callback, onClose })`
 *   3. On `callback({ reference })` → POST /v1/wallet/wallets-mine/cash-ins/verify
 *   4. Backend re-checks Paystack and credits the wallet idempotently
 *
 * Graceful degrade: when `env.paystackPublicKey === ''` (dev / no key) the page
 * skips the popup entirely and posts straight to /verify with the locally
 * issued reference — the @upmp/paystack-client test-mode stub on the backend
 * always returns `status === 'success'` so the wallet is still credited.
 *
 * RBAC: `Wallet/credit-own` (seeded by 20260514_citizen_pay_perms.sql).
 * Amount limits: min ₦500, max ₦500,000 per SRS §cash-in.
 */
import { useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDownLeft,
  Building2,
  CheckCircle2,
  CreditCard,
  Hash,
  Loader2,
} from 'lucide-react';

import {
  citizenCashInInitialize,
  citizenCashInVerify,
  type CitizenCashInInitializeResponse,
  type CitizenCashInVerifyResponse,
} from '../../lib/api/wallet';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { env } from '../../lib/env';
import {
  PageHeader,
  customerWalletQueryKey,
  describeWalletError,
  formatNaira,
  useCustomerWallet,
} from './NipostPayUI';

// ---------------------------------------------------------------------------
// Paystack Inline ambient declaration. We don't import their SDK as a build-
// time dep — instead we inject the <script> on first use (see loadPaystackPop).
// ---------------------------------------------------------------------------

interface PaystackPopHandle {
  openIframe(): void;
}

interface PaystackPopSetupOptions {
  key: string;
  email: string;
  // eslint-disable-next-line upmp/no-number-money -- third-party Paystack inline SDK contract uses number for kobo; we cannot change the shape it requires
  amount: number; // kobo
  ref: string;
  currency?: string;
  callback: (response: { reference: string }) => void;
  onClose: () => void;
  metadata?: Record<string, unknown>;
}

interface PaystackPopApi {
  setup(opts: PaystackPopSetupOptions): PaystackPopHandle;
}

/** Structural type guard for `window.PaystackPop` — `card-payments.ts`
 *  declares a different shape for the v2 inline modal API, so we deliberately
 *  avoid `declare global` and just narrow on read instead. */
function windowPaystackPop(): PaystackPopApi | undefined {
  if (typeof window === 'undefined') return undefined;
  const candidate = (window as unknown as { PaystackPop?: unknown }).PaystackPop;
  if (
    candidate &&
    typeof candidate === 'object' &&
    typeof (candidate as { setup?: unknown }).setup === 'function'
  ) {
    return candidate as PaystackPopApi;
  }
  return undefined;
}

const PAYSTACK_INLINE_SRC = 'https://js.paystack.co/v1/inline.js';

/** Lazily inject the Paystack Inline script. Resolves once `window.PaystackPop`
 *  is available. Safe to call multiple times — re-uses the existing tag. */
function loadPaystackPop(): Promise<PaystackPopApi> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Paystack Inline only works in the browser'));
  }
  const already = windowPaystackPop();
  if (already) {
    return Promise.resolve(already);
  }
  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${PAYSTACK_INLINE_SRC}"]`,
  );
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => {
        const pop = windowPaystackPop();
        if (pop) resolve(pop);
        else reject(new Error('Paystack Inline script loaded but PaystackPop is unavailable'));
      });
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Paystack Inline')),
      );
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PAYSTACK_INLINE_SRC;
    script.async = true;
    script.onload = () => {
      const pop = windowPaystackPop();
      if (pop) resolve(pop);
      else reject(new Error('Paystack Inline script loaded but PaystackPop is unavailable'));
    };
    script.onerror = () => reject(new Error('Failed to load Paystack Inline'));
    document.body.appendChild(script);
  });
}

const QUICK_AMOUNTS: readonly number[] = [1000, 5000, 10000, 50000];

type Channel = 'card' | 'bank' | 'ussd';

const CHANNELS: readonly { key: Channel; label: string; icon: typeof CreditCard }[] = [
  { key: 'card', label: 'Card', icon: CreditCard },
  { key: 'bank', label: 'Bank transfer', icon: Building2 },
  { key: 'ussd', label: 'USSD', icon: Hash },
];

const MIN_AMOUNT = 500;
const MAX_AMOUNT = 500_000;

type Outcome =
  | { kind: 'idle' }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string }
  | { kind: 'success'; verify: CitizenCashInVerifyResponse };

export function CashInPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.upmpUserId ?? '';
  const email = claims?.email ?? '';
  const wallet = useCustomerWallet(customerId, token);

  const [amount, setAmount] = useState<string>('');
  const [channel, setChannel] = useState<Channel>('card');
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });
  const [popupOpen, setPopupOpen] = useState<boolean>(false);

  const paystackKey = env.paystackPublicKey;
  const useDeterministicMode = paystackKey.length === 0;

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- React Query's useMutation third generic is the variables type; `void` is the canonical "no variables" marker
  const initMutation = useMutation<CitizenCashInInitializeResponse, Error, void>({
    mutationFn: () =>
      citizenCashInInitialize(
        { amount: amount.length > 0 ? amount : '0', currency: 'NGN' },
        { bearerToken: token ?? undefined },
      ),
  });

  const verifyMutation = useMutation<CitizenCashInVerifyResponse, Error, string>({
    mutationFn: (reference: string) =>
      citizenCashInVerify({ reference }, { bearerToken: token ?? undefined }),
    onSuccess: (verify) => {
      setOutcome({ kind: 'success', verify });
      queryClient.invalidateQueries({ queryKey: customerWalletQueryKey(customerId) });
    },
    onError: (err) => {
      setOutcome({ kind: 'error', message: describeWalletError(err) });
    },
  });

  // eslint-disable-next-line upmp/no-number-money -- validation only
  const amountNum = amount ? Number(amount) : 0;
  const amountError =
     
    amountNum > 0 && amountNum < MIN_AMOUNT
      ? `Minimum top-up is ${formatNaira(MIN_AMOUNT)}`
       
      : amountNum > MAX_AMOUNT
        ? `Maximum top-up is ${formatNaira(MAX_AMOUNT)}`
        : null;

  const busy = initMutation.isPending || verifyMutation.isPending || popupOpen;

  async function startCashIn(): Promise<void> {
    setOutcome({ kind: 'idle' });

    let init: CitizenCashInInitializeResponse;
    try {
      init = await initMutation.mutateAsync();
    } catch (err) {
      setOutcome({
        kind: 'error',
        message: describeWalletError(err instanceof Error ? err : null),
      });
      return;
    }

    if (useDeterministicMode) {
      // Skip the popup — backend deterministic stub will accept the reference.
      verifyMutation.mutate(init.reference);
      return;
    }

    let PaystackPop: PaystackPopApi;
    try {
      PaystackPop = await loadPaystackPop();
    } catch (err) {
      setOutcome({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Could not load Paystack',
      });
      return;
    }

    const handle = PaystackPop.setup({
      key: init.publicKey,
      // eslint-disable-next-line upmp/no-number-money -- kobo conversion for Paystack popup, server is the source of truth.
      amount: Math.round(Number(init.amount) * 100),
      currency: init.currency,
      email: email.length > 0 ? email : `citizen-${customerId.slice(0, 12)}@nipost.local`,
      ref: init.reference,
      metadata: { citizenId: customerId },
      callback: (resp) => {
        setPopupOpen(false);
        verifyMutation.mutate(resp.reference);
      },
      onClose: () => {
        setPopupOpen(false);
        setOutcome({ kind: 'cancelled' });
      },
    });
    setPopupOpen(true);
    handle.openIframe();
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
     
    if (!amount || amountNum <= 0 || amountError) return;
    if (channel !== 'card') return; // bank / ussd not wired in this slice
    void startCashIn();
  }

  return (
    <div>
      <PageHeader title="Cash in" description="Top up your NIPOST Pay wallet from a card, bank, or USSD." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-border p-6">
            <p className="text-sm text-muted mb-1">Current balance</p>
            {wallet.isLoading ? (
              <p className="text-sm text-muted inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            ) : wallet.isError ? (
              <p className="text-sm text-red-600">{describeWalletError(wallet.error)}</p>
            ) : wallet.data ? (
              <p className="text-4xl font-bold text-nipost-green">
                {formatNaira(wallet.data.balanceCached ?? wallet.data.balance ?? '0')}
              </p>
            ) : null}
          </div>

          {useDeterministicMode ? (
            <div
              className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 flex items-start gap-2"
              role="status"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Paystack not configured — using deterministic test mode. Card payments will
                succeed instantly without opening the Paystack popup. Set
                <code className="mx-1 px-1 bg-amber-100 rounded">VITE_PAYSTACK_PUBLIC_KEY</code>
                to enable the real Paystack flow.
              </span>
            </div>
          ) : null}

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-xl border border-border p-6 space-y-5"
            noValidate
          >
            <h3 className="font-semibold text-gray-900">Amount</h3>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-gray-500">
                ₦
              </span>
              <input
                type="number"
                min={MIN_AMOUNT}
                max={MAX_AMOUNT}
                step="100"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                aria-label="Amount"
                aria-describedby={amountError ? 'amount-error' : undefined}
                className="w-full pl-10 pr-4 py-4 text-2xl font-semibold border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
            </div>
            {amountError ? (
              <p id="amount-error" className="text-xs text-red-600" role="alert">{amountError}</p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {QUICK_AMOUNTS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setAmount(String(q))}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border ${
                    String(q) === amount
                      ? 'bg-nipost-green text-white border-nipost-green'
                      : 'border-border text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  ₦{q.toLocaleString()}
                </button>
              ))}
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Channel</h3>
              <div className="flex border-b border-border mb-4">
                {CHANNELS.map((c) => {
                  const Icon = c.icon;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setChannel(c.key)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px ${
                        channel === c.key
                          ? 'border-nipost-green text-nipost-green'
                          : 'border-transparent text-muted hover:text-gray-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" /> {c.label}
                    </button>
                  );
                })}
              </div>

              {channel !== 'card' ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {channel === 'bank'
                    ? 'Bank transfer rail is not wired yet — pick "Card" to top up via Paystack.'
                    : 'USSD: dial *737*1*amount*PIN# to top up from your mobile network.'}
                </p>
              ) : null}
            </div>

            {outcome.kind === 'error' ? (
              <p
                className="text-xs text-red-600 rounded-lg bg-red-50 border border-red-200 px-3 py-2"
                role="alert"
              >
                {outcome.message}
              </p>
            ) : null}

            {outcome.kind === 'cancelled' ? (
              <p
                className="text-xs text-gray-600 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2"
                role="status"
              >
                Top-up cancelled — your wallet was not charged.
              </p>
            ) : null}

            {outcome.kind === 'success' ? (
              <div
                className="rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2"
                role="status"
                aria-live="polite"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Wallet topped up — new balance{' '}
                <strong>{formatNaira(outcome.verify.walletBalance)}</strong>
                {' '}(txn {outcome.verify.txnNumber.slice(0, 12)})
              </div>
            ) : null}

            <button
              type="submit"
              disabled={
                 
                !amount || amountNum <= 0 || !!amountError || busy || !wallet.data || channel !== 'card'
              }
              className="w-full py-3 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownLeft className="w-4 h-4" />}
              {/* eslint-disable-next-line upmp/no-number-money -- display-only */}
              Top up with card{amount ? ` — ₦${Number(amount).toLocaleString()}` : ''}
            </button>
          </form>
        </div>

        <div>
          <div className="bg-white rounded-xl border border-border p-5 lg:sticky lg:top-24">
            <h3 className="font-semibold text-gray-900 mb-3">How cash in works</h3>
            <ul className="text-sm text-muted space-y-2">
              <li>1. Pick an amount.</li>
              <li>2. Enter your card in the Paystack popup.</li>
              <li>3. Your wallet is credited the moment Paystack confirms.</li>
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-muted space-y-1">
              <p>Minimum: {formatNaira(MIN_AMOUNT)}</p>
              <p>Maximum: {formatNaira(MAX_AMOUNT)} per top-up</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
